import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Button, { ButtonTypes } from 'components/common/button';
import useDevice from 'components/hooks/use_device';
import UniversalSearchBarContainer from 'components/search/universal_search_bar_container';
import UniversalSearchFilterContainer from 'components/search/universal_search_filter_container';
import UniversalSearchResultsContainer from 'components/search/universal_search_results_container';
import { Device } from 'scripts/lib/device_detector';

export default function UniversalSearch({
  filterHits,
  onSearchQueryChange,
  onSelectItem,
  placeholder,
  searchResultId,
}) {
  const isPhone = useDevice() === Device.Phone;
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleSearchTextChange = useCallback(
    text => {
      onSearchQueryChange({ text });
      window.scrollTo(0, 0);
    },
    [onSearchQueryChange]
  );

  const handleSearchFilterChange = useCallback(
    filterProperties => {
      onSearchQueryChange({ filterProperties });
      window.scrollTo(0, 0);
    },
    [onSearchQueryChange]
  );

  const toggleFilters = useCallback(() => setFiltersOpen(prev => !prev), []);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  return (
    <div className="universalSearch-searchPage">
      {!isPhone && (
        <UniversalSearchFilterContainer onSearchFilterChange={handleSearchFilterChange} queryId={searchResultId} />
      )}
      <div className="universalSearch-searchBarResultsContainer">
        <SearchBarRow>
          <UniversalSearchBarContainer
            onSearchTextChange={handleSearchTextChange}
            placeholder={placeholder}
            queryId={searchResultId}
          />
          {isPhone && (
            <FilterButton buttonType={ButtonTypes.SECONDARY} data-aid="search-filter-toggle" onClick={toggleFilters}>
              <FilterIcon aria-hidden="true" focusable="false" viewBox="0 0 16 16">
                <path d="M1 2.5h14L9.5 9v4.5l-3 1.5V9L1 2.5z" />
              </FilterIcon>
              Filters
            </FilterButton>
          )}
        </SearchBarRow>
        <UniversalSearchResultsContainer
          filterHits={filterHits}
          onSelectItem={onSelectItem}
          queryId={searchResultId}
          shouldShowPagination
        />
      </div>
      {isPhone && filtersOpen && (
        <FilterOverlay aria-label="Filter options" aria-modal="true" data-aid="search-filter-overlay" role="dialog">
          <FilterOverlayHeader>
            <FilterOverlayTitle>Filters</FilterOverlayTitle>
            <FilterCloseButton
              aria-label="Close filters"
              data-aid="search-filter-close"
              onClick={closeFilters}
              type="button"
            >
              &times;
            </FilterCloseButton>
          </FilterOverlayHeader>
          <FilterOverlayBody>
            <UniversalSearchFilterContainer onSearchFilterChange={handleSearchFilterChange} queryId={searchResultId} />
          </FilterOverlayBody>
        </FilterOverlay>
      )}
    </div>
  );
}

UniversalSearch.propTypes = {
  filterHits: PropTypes.func,
  onSearchQueryChange: PropTypes.func.isRequired,
  onSelectItem: PropTypes.func,
  placeholder: PropTypes.string,
  searchResultId: PropTypes.string,
};

const SearchBarRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;

  .universalSearch-searchInput-wrapper {
    flex: 1;
    min-width: 0;
  }

  [data-device='phone'] & {
    .universalSearch-searchInput-wrapper {
      margin-right: 0;
    }
  }
`;

const FilterButton = styled(Button)`
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  gap: 6px;
  margin-right: 12px;
  margin-top: 28px;
`;

const FilterIcon = styled.svg`
  fill: currentColor;
  height: 14px;
  width: 14px;
`;

const FilterOverlay = styled.div`
  background: var(--color-white);
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10;
`;

const FilterOverlayHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--color-gray200);
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  padding: 16px;
`;

const FilterOverlayTitle = styled.div`
  font-size: var(--font-size-large);
  font-weight: 600;
`;

const FilterCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 28px;
  line-height: 1;
  padding: 0 4px;
`;

const FilterOverlayBody = styled.div`
  background: var(--color-feed-background);
  flex: 1;
  overflow-y: auto;

  .universalSearch-filters-wrapper {
    border-right: none;
    min-width: unset;
    width: 100%;
  }
`;
